// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import AdminUser from 'AdminUser';
import {
  PermissionDefinition,
  UserPermissions,
  VeraPromptProposal,
  VeraIntentPromptVersion,
  VeraGlobalChatPromptOverride,
} from '../../models/admin';
export const adminBackendServiceAdminUserMixin = {
  getAdminUser(id: string): Promise<AdminUser> {
    return this.authApiGetAuthed(
      `adminUsers/${id}`,
      'Network error while fetching admin user',
      (data) => data.data as AdminUser,
    );
  },

  updateAdminUser(id: string, data: AdminUserUpdate): Promise<void> {
    return this.authApiMutateAuthed<void>(`adminUsers/${id}`, 'PATCH', data, () => undefined);
  },

  createAdminUserWithEmailAndPassword(invitationToken: string, password: string): Promise<void> {
    return this.authenticatedFetch(this.apiURL(`adminUsers/invitation/accept/${invitationToken}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    }).then(async (response) => {
      if (response.status === 200 || response.status === 304) return;
      if (response.status === 404) throw 'Admin invitation not found';
      const data = await response.json();
      throw data.error?.message || 'Unknown error';
    });
  },

  createAdminUser(adminUser: AdminUserUpdate): Promise<string> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('adminUsers'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(adminUser),
      });
      if (!response.ok) {
        await new Promise<void>((_resolve, reject) => {
          this.handleErrorResponse(response, reject);
        });
      }
      const data = await response.json();
      if (data?.data?.id) {
        return data.data.id as string;
      }
      throw 'Missing ID in response';
    });
  },

  searchAdminUser(searchTerm: string): Promise<AdminUser[]> {
    return this.authApiGetAuthed(
      `adminUsers/search?term=${encodeURIComponent(searchTerm)}`,
      'Network error while searching admin users',
      (data) => data.data as AdminUser[],
    );
  },

  getAllPermissions(): Promise<PermissionDefinition[]> {
    return this.authApiGetAuthed(
      'admin/permissions',
      'Failed to fetch permissions',
      (json) => json.data as PermissionDefinition[],
    );
  },

  getAdminUserPermissions(adminUserId: string): Promise<UserPermissions> {
    return this.authApiGetAuthed(
      `adminUsers/${adminUserId}/permissions`,
      'Failed to fetch user permissions',
      (json) => json.data.actionGrants as UserPermissions,
    );
  },

  updateAdminUserPermissions(adminUserId: string, permissions: UserPermissions): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `adminUsers/${adminUserId}/permissions`,
      'PATCH',
      permissions,
      () => undefined,
    );
  },

  async getAllAdminUsers(): Promise<AdminUser[]> {
    return this.authApiGetAuthed(
      '/api/admin/users',
      'Network error while fetching admin users',
      (json) => (json.data || []) as AdminUser[],
      true,
    );
  },

  async getReviewers(searchQuery?: string): Promise<AdminUser[]> {
    const url = searchQuery
      ? `/api/adminUsers/reviewers?search=${encodeURIComponent(searchQuery)}`
      : '/api/adminUsers/reviewers';
    return this.authApiGetAuthed(
      url,
      'Network error while fetching reviewers',
      (json) => (json.data || []) as AdminUser[],
      true,
    );
  },
} satisfies ThisType<BaseBackendService>;
